import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col, Table, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import { useCreateBoardConfig, useUpdateBoardConfig } from '../../../../services/supportAdmin';
import { useForm } from '../../../../hooks/useForm';
import MondayBoardSelector from '../components/MondayBoardSelector';
import MondayColumnSelector from '../components/MondayColumnSelector';

const DEFAULTS = {
  mondayBoardId: '',
  boardName: '',
  submittedByColumnId: '',
  emailColumnId: '',
  filesColumnId: '',
};

const BoardConfigModal = ({ show, toggle, boardConfig, onSuccess }) => {
  const { form, onChange, setValues } = useForm(DEFAULTS);
  const [statusMappings, setStatusMappings] = useState([]);
  const [newStatusLabel, setNewStatusLabel] = useState('');
  const [newStatusCategory, setNewStatusCategory] = useState('closed');

  useEffect(() => {
    if (boardConfig && boardConfig.configId) {
      setValues({
        ...DEFAULTS,
        mondayBoardId: boardConfig.mondayBoardId || '',
        boardName: boardConfig.boardName || '',
        submittedByColumnId: boardConfig.submittedByColumnId || '',
        emailColumnId: boardConfig.emailColumnId || '',
        filesColumnId: boardConfig.filesColumnId || '',
      });
      setStatusMappings(boardConfig.statusMappings || []);
    } else {
      setValues(DEFAULTS);
      setStatusMappings([]);
    }
    setNewStatusLabel('');
    setNewStatusCategory('closed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardConfig]);

  const createConfig = useCreateBoardConfig(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Board config created successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.response?.data?.message || err.message) },
  );

  const updateConfig = useUpdateBoardConfig(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Board config updated successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.response?.data?.message || err.message) },
  );

  const handleAddStatusMapping = () => {
    if (!newStatusLabel.trim()) {
      toast.error('Please enter a status label.');
      return;
    }

    // Check for duplicate
    if (statusMappings.some((m) => m.statusLabel.toLowerCase() === newStatusLabel.trim().toLowerCase())) {
      toast.error('This status label is already mapped.');
      return;
    }

    setStatusMappings([
      ...statusMappings,
      { statusLabel: newStatusLabel.trim(), category: newStatusCategory },
    ]);
    setNewStatusLabel('');
  };

  const handleRemoveStatusMapping = (index) => {
    setStatusMappings(statusMappings.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (index, newCategory) => {
    setStatusMappings(
      statusMappings.map((m, i) => (i === index ? { ...m, category: newCategory } : m)),
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!form.mondayBoardId) {
      toast.error('Please select a Monday board.');
      return;
    }

    const payload = {
      mondayBoardId: form.mondayBoardId,
      boardName: form.boardName || null,
      submittedByColumnId: form.submittedByColumnId || null,
      emailColumnId: form.emailColumnId || null,
      filesColumnId: form.filesColumnId || null,
      statusMappings,
    };

    if (boardConfig && boardConfig.configId) {
      updateConfig.mutate({
        configId: boardConfig.configId,
        payload,
      });
    } else {
      createConfig.mutate(payload);
    }
  };

  const isEditing = boardConfig && boardConfig.configId;
  const isLoading = createConfig.isLoading || updateConfig.isLoading;

  const closedCount = statusMappings.filter((m) => m.category === 'closed').length;

  return (
    <Modal show={show} onHide={() => toggle()} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Board Config' : 'Add Board Config'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="board-config-form" onSubmit={onSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Monday Board *</Form.Label>
                <MondayBoardSelector
                  value={form.mondayBoardId}
                  onChange={(boardId, boardName) => {
                    setValues({
                      ...form,
                      mondayBoardId: boardId,
                      boardName: boardName || '',
                      submittedByColumnId: '',
                      emailColumnId: '',
                      filesColumnId: '',
                    });
                  }}
                  disabled={isEditing}
                />
                {isEditing && (
                  <Form.Text className="text-muted">
                    Board cannot be changed after creation
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Display Name</Form.Label>
                <Form.Control
                  name="boardName"
                  onChange={onChange}
                  value={form.boardName}
                  placeholder="Optional display name for this board"
                />
              </Form.Group>
            </Col>
          </Row>

          {form.mondayBoardId && (
            <>
              <hr />
              <h6>Column Mappings</h6>
              <p className="text-muted small mb-3">
                Configure which Monday columns to use for common fields. These apply to all forms using this board.
              </p>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Submitted By Column</Form.Label>
                    <MondayColumnSelector
                      boardId={form.mondayBoardId}
                      value={form.submittedByColumnId}
                      onChange={(columnId) => setValues({ ...form, submittedByColumnId: columnId })}
                      filterTypes={['text', 'long_text']}
                      placeholder="Select column for submitter name..."
                    />
                    <Form.Text className="text-muted">
                      Auto-fills with user's name
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Submitter Email Column</Form.Label>
                    <MondayColumnSelector
                      boardId={form.mondayBoardId}
                      value={form.emailColumnId}
                      onChange={(columnId) => setValues({ ...form, emailColumnId: columnId })}
                      filterTypes={['email', 'text']}
                      placeholder="Select column for submitter email..."
                    />
                    <Form.Text className="text-muted">
                      Auto-fills with user's email
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Files Column</Form.Label>
                    <MondayColumnSelector
                      boardId={form.mondayBoardId}
                      value={form.filesColumnId}
                      onChange={(columnId) => setValues({ ...form, filesColumnId: columnId })}
                      filterTypes={['file']}
                      placeholder="Select column for file uploads..."
                    />
                    <Form.Text className="text-muted">
                      Where uploaded files will be stored
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <h6>
                Status Mappings{' '}
                {closedCount > 0 && (
                  <Badge bg="secondary" className="ms-2">
                    {closedCount} closed
                  </Badge>
                )}
              </h6>
              <p className="text-muted small mb-3">
                Define which Monday status labels should be considered "Closed". All unmapped statuses are treated as "Open".
              </p>

              {statusMappings.length > 0 && (
                <Table size="sm" className="mb-3">
                  <thead>
                    <tr>
                      <th>Status Label</th>
                      <th style={{ width: '150px' }}>Category</th>
                      <th style={{ width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusMappings.map((mapping, index) => (
                      <tr key={index}>
                        <td>{mapping.statusLabel}</td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={mapping.category}
                            onChange={(e) => handleCategoryChange(index, e.target.value)}
                          >
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => handleRemoveStatusMapping(index)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <Row className="align-items-end">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Add Status Mapping</Form.Label>
                    <Form.Control
                      size="sm"
                      value={newStatusLabel}
                      onChange={(e) => setNewStatusLabel(e.target.value)}
                      placeholder="e.g., Closed Complete"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddStatusMapping();
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Form.Group className="mb-3">
                    <Form.Select
                      size="sm"
                      value={newStatusCategory}
                      onChange={(e) => setNewStatusCategory(e.target.value)}
                    >
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mb-3"
                    onClick={handleAddStatusMapping}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </Button>
                </Col>
              </Row>

              <div className="alert alert-info small mb-0">
                <strong>Note:</strong> If no status mappings are configured, the system falls back to treating
                "Closed Complete" and "Closed Incomplete" as closed statuses.
              </div>
            </>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => toggle()}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          form="board-config-form"
          disabled={isLoading}
        >
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faCheck}
            spin={isLoading}
          />{' '}
          {isEditing ? 'Update Config' : 'Add Config'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BoardConfigModal;
